import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getProductDetailsType } from "../../components/sellScreen/productDetailsModule";
import { checkCustomerPhoneNumberResponse, getAllEligibleDiscountPayload, getAllEligibleDiscountResponse, overAllDiscount } from "../../components/sellScreen/sellScreenModule";
import { ZenToast } from "zen-core-elements";

// Types
import { addCartProductType, ApiError, discountType, initialStateType, ReturnRequestWholeOrder } from "../types/sellScreenTypes";

// Utility functions
import {
	discountValueCalculation,
	applyCartDiscountToProducts,
	updateCartTotalAndQuantity,
	calculateSummary,
	getOfferValue,
	applyManualDiscount,
	remainingState,
	addVariantToCart
} from "../utils/discountUtils";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { API } from "../../shared/apiRoutes";
import type { AppDispatch, RootState } from "../store";
import { config } from "../../shared/environment";
import { BuyXGetYDiscount, calculateCart, emptyDiscountStore, hasVariantDiscounts, mergeVariantDiscounts, normaliseProductDiscount, retainVariants } from "../utils/discountEngine";
import { RawProductDiscount } from "../utils/discountEngine/rawTypes";
import { buildEngineInput, repriceCart, usesDiscountEngine } from "../utils/sellscreenPricing";

const initialState: initialStateType = {
	cartProductIds: {},
	cartProducts: {},
	cartQuantity: {},
	cartTotal: {},
	automaticDiscount: {},
	manualDiscount: {},
	additionalDiscount: {},
	additionalCharge: {},
	tip: {},
	summary: {},
	customerNotes: {},
	claimedOffers: {},
	automaticChoice: {},
	pricedCart: {},
	parkSale: [],
	customerDetails: null,
	sellScreenType: "SELL",
	getAllEligibleDiscount: [],

	productsDetailsById: {},
	discountStore: {},
	roundOffSale: false,
	productDetailsLoader: false,
	returnRequestData: null,
	returnRequestWholeOrderData: null
};

export interface OrderStatusEventPayload {
	customizedOrderId: string;
	event: string;
	isPreOrder: boolean;
	modifiedAt: string; // ISO 8601 date string
	orderId: number;
	orderName: string;
	orderStatus: number;
	orderStatusCode: string;
	orderStatusDate: string; // ISO 8601 date string
	storeId: number;
	todayDate: string; // Formatted date string (e.g., "MMMM DD, YYYY - hh:mm A")
	userEmail: string;
}


export const getAllEligibleDiscount = createAsyncThunk("NewSellscreenSlice/getAllEligibleDiscount", async ({ storeId, payload }: { storeId: number, payload: getAllEligibleDiscountPayload }, { getState }) => {
	let url = HttpRoutingService?.replaceUrl(API.getAllEligibleDiscount, { storeId })
	const response = await HttpRoutingService?.postMethod<getAllEligibleDiscountResponse>(url, payload)
	// Captured here because the store cannot be read while the reducer is executing
	const currencySymbol: string = (getState() as RootState)?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol ?? '₹'
	return { ...response, currencySymbol }
})

export const checkCustomerPhoneNumber = createAsyncThunk("NewSellscreenSlice/checkCustomerPhoneNumber", ({ storeId, payload }: { storeId: number, payload: { countryCode: number, contactNumber: number } }) => {
	let url = HttpRoutingService?.replaceUrl(API.checkCustomerPhoneNumber, { storeId })
	return HttpRoutingService?.getMethod<checkCustomerPhoneNumberResponse>(url, payload)
})

export const returnOrderForSellScreen = createAsyncThunk("NewSellscreenSlice/returnOrderForSellScreen", ({ storeId, orderId, payload }: { storeId: number, orderId: number, payload: any }) => {
	let url = HttpRoutingService?.replaceUrl(API.returnOrderForSellScreen, { storeId, orderId })
	return HttpRoutingService?.postMethod<any>(url, payload, 'v2/')
})
export const getProductDetailsById = createAsyncThunk<
	getProductDetailsType,
	{ storeId: number, productId: number, locationId: number, isFromBarcode?: boolean, skuId?: string },
	{ state: RootState, rejectValue: ApiError }
>(
	'NewSellscreenSlice/getProductDetailsById', async ({ storeId, productId, locationId, isFromBarcode, skuId }: { storeId: number, productId: number, locationId: number, isFromBarcode?: boolean, skuId?: string }, { rejectWithValue }) => {
		try {
			const url = HttpRoutingService?.replaceUrl(isFromBarcode ? API?.getOnePosProductForSellScreenBySku : API?.getOnePosProductForSellScreen, isFromBarcode ? { storeId: storeId, skuId: skuId } : { storeId: storeId, productId: productId })
			return await HttpRoutingService?.getMethod(url, { locationId })
		} catch (err) {
			return rejectWithValue(err as ApiError);
		}
	},
);

export const getOneVariantDiscount = createAsyncThunk("NewSellscreenSlice/getOneVariantDiscount", ({ storeId, productId, variantId, locationId }: { storeId: number, productId: number, variantId: number, locationId: number }) => {
	const url = HttpRoutingService?.replaceUrl(API?.getOnePosProductDiscount, { storeId: storeId, productId: productId, variantId: variantId })
	return HttpRoutingService?.getMethod<{ productDiscount: RawProductDiscount }>(url, { locationId })
})

// Fetches a variant's discounts once; later adds of the same variant reuse the cache
export const ensureVariantDiscounts = createAsyncThunk<void, { productId: number, variantId: number, locationId: number, force?: boolean }, { state: RootState }>(
	"NewSellscreenSlice/ensureVariantDiscounts", async ({ productId, variantId, locationId, force }, { dispatch, getState }) => {
		if (!force && hasVariantDiscounts(getState().NewSellscreenSlice.discountStore?.[locationId], variantId)) return
		await dispatch(getOneVariantDiscount({ storeId: config?.storeId, productId, variantId, locationId })).unwrap()
	}
)

// Refetch every cart line's discounts: customer changed, held order resumed, or the server rejected a discount
export const refreshCartDiscounts = createAsyncThunk<void, { locationId: number }, { state: RootState }>(
	"NewSellscreenSlice/refreshCartDiscounts", async ({ locationId }, { dispatch, getState }) => {
		const state = getState().NewSellscreenSlice
		if (!usesDiscountEngine(state, locationId)) return
		const lines = state.cartProducts?.[locationId] ?? []
		await Promise.allSettled(lines.map((item) =>
			dispatch(getOneVariantDiscount({ storeId: config?.storeId, productId: item.productId, variantId: item.variantId, locationId })).unwrap()
		))
		dispatch(repriceSale({ locationId }))
	}
)

// Claim a Buy X Get Y offer: makes sure enough get units are in the cart, then records the claim
export const claimOfferForCart = createAsyncThunk<void, { locationId: number, discountId: number, getVariantId: number, getProductId: number }, { state: RootState, rejectValue: string }>(
	"NewSellscreenSlice/claimOfferForCart", async ({ locationId, discountId, getVariantId, getProductId }, { dispatch, getState, rejectWithValue }) => {
		const state = getState().NewSellscreenSlice
		const input = buildEngineInput(state, locationId)
		const discount = input.discounts.find((item) => item.id === discountId) as BuyXGetYDiscount | undefined
		if (!discount) return rejectWithValue("This offer is no longer available.")
		const prospective = calculateCart({ ...input, claimedOffers: [...input.claimedOffers, { discountId, getVariantId }], previousAppliedDiscountIds: undefined })
		const result = prospective.claims[prospective.claims.length - 1]
		const missingGetItem = result.reason === "GET_ITEM_NOT_IN_CART" || result.reason === "NO_GET_UNITS"
		if (result.status === "DROPPED" && !missingGetItem) {
			const message = prospective.messages.find((item) => item.discountId === discountId)?.text || "This offer cannot be claimed."
			return rejectWithValue(message)
		}
		const shortfall = result.status === "DROPPED" ? discount.get.quantity : Math.max(0, discount.get.quantity - result.getUnits)
		if (shortfall > 0) {
			const existing = state.cartProducts?.[locationId]?.find((item) => item.variantId === getVariantId)
			if (existing) {
				dispatch(updateCartProductQuantity({ variantId: getVariantId, quantity: existing.quantity + shortfall, locationId }))
			} else {
				const added = await addVariantToCart({ dispatch: dispatch as AppDispatch, locationId, productId: getProductId, variantId: getVariantId, quantity: shortfall })
				if (!added) return rejectWithValue("The offer item could not be added to the cart.")
			}
		}
		dispatch(claimOffer({ locationId, discountId, getVariantId }))
	}
)

// Re-applies discounts for legacy (return / replacement) carts, unchanged from before the engine
const legacyReapply = (state: initialStateType, locationId: number) => {
	updateCartTotalAndQuantity({ state, locationId });
	if (state.manualDiscount?.[locationId]?.promoCode) {
		applyManualDiscount({ state, locationId })
	} else if (state.additionalDiscount?.[locationId]?.type) {
		applyCartDiscountToProducts({ state, discountAmount: state.additionalDiscount?.[locationId]?.discountAmount, locationId });
	} else {
		calculateSummary({ state, locationId });
	}
}

const forgetLine = (state: initialStateType, locationId: number, variantId: number) => {
	if (state.automaticChoice?.[locationId]) delete state.automaticChoice[locationId][String(variantId)]
}

const newSellscreenSlice = createSlice({
	name: "NewSellscreenSlice",
	initialState,
	reducers: {
		addCartProduct: (state, action: PayloadAction<{ product: addCartProductType, locationId: number }>) => {
			const locationId = action.payload.locationId
			const { variantId, quantity, productId } = action.payload.product;
			if (!state.cartProductIds[locationId]) {
				state.cartProductIds[locationId] = {};
			}
			if (!state.cartProductIds[locationId][variantId]) {
				state.cartProductIds[locationId][variantId] = {
					quantity,
					productId,
				};
			}
			if (!state.cartProducts[locationId]) {
				state.cartProducts[locationId] = []
			}
			state.cartProducts[locationId].push({ ...action.payload.product });

			if (usesDiscountEngine(state, locationId)) {
				repriceCart(state, locationId)
				return
			}
			legacyReapply(state, locationId)
		},
		updateCartProductQuantity: (state, action: PayloadAction<{ variantId: number; quantity: number, locationId: number, isBarcode?: boolean }>) => {
			const locationId = action.payload.locationId
			const { variantId, quantity, isBarcode } = action.payload;
			if (!quantity) {
				const index = state.cartProducts[locationId].findIndex((p) => p.variantId === variantId);
				if (index !== -1) {
					state.cartProducts[locationId].splice(index, 1);
					delete state.cartProductIds[locationId][variantId];
					forgetLine(state, locationId, variantId)
				}
			} else {
				const product = state.cartProducts[locationId].find((p) => p.variantId === variantId);
				if (product) {
					const isNegative = product.quantity < 0 || quantity < 0;
					const correctQuantity = isNegative ? quantity : Math.max(0, quantity);
					let finalQuantity = correctQuantity
					if (isBarcode) {
						finalQuantity = correctQuantity + product?.weightCount
					}
					const stock = product.stock;
					const maxStock = stock === -1 ? Infinity : stock;
					if (finalQuantity <= maxStock) {
						product.quantity = finalQuantity;
						state.cartProductIds[locationId][variantId] = { quantity: finalQuantity, productId: product.productId };
						product.finalPrice = product.price * finalQuantity;
					} else {
						ZenToast({ type: "warning", message: `Stock limit exceeded (Max: ${stock})` });
					}
				}
			}
			if (usesDiscountEngine(state, locationId)) {
				repriceCart(state, locationId)
				return
			}
			legacyReapply(state, locationId)
		},
		deleteCartProduct: (state, action: PayloadAction<{ variantId: number, locationId: number }>) => {
			const variantId = action.payload.variantId;
			const locationId = action.payload.locationId
			const index = state.cartProducts[locationId].findIndex((p) => p.variantId === variantId);

			if (index !== -1) {
				state.cartProducts[locationId].splice(index, 1);
				delete state.cartProductIds[locationId][variantId];
				forgetLine(state, locationId, variantId)
			}
			if (usesDiscountEngine(state, locationId)) {
				repriceCart(state, locationId)
				return
			}
			legacyReapply(state, locationId)
		},
		addManualDiscount: (state, action: PayloadAction<{ discount: overAllDiscount | null, locationId: number }>) => {
			const locationId = action.payload.locationId
			if (usesDiscountEngine(state, locationId)) {
				state.manualDiscount[locationId] = action.payload.discount ? { ...action.payload.discount, isValidate: false, discountAmount: 0 } : null
				repriceCart(state, locationId)
				return
			}
			if (!action.payload.discount) {
				state.manualDiscount[locationId] = null;
				applyCartDiscountToProducts({ state, discountAmount: 0, locationId });
				return;
			}
			const discountAmount = discountValueCalculation({
				discountTypeCode: action.payload.discount.discountType?.discountTypeCode as discountType,
				discountValue: Number(action.payload.discount.discountValue),
				cartTotal: state.cartTotal[locationId],
				maxAmount: action.payload.discount.maxAmount ?? null,
			});
			state.manualDiscount[locationId] = { ...action.payload.discount, isValidate: true, discountAmount };
			applyCartDiscountToProducts({ state, discountAmount, locationId });
		},
		addAdditionalDiscount: (state, action: PayloadAction<{ discount: initialStateType["additionalDiscount"][number], locationId: number }>) => {
			const locationId = action.payload.locationId
			if (usesDiscountEngine(state, locationId)) {
				state.additionalDiscount[locationId] = action.payload.discount ? { ...action.payload.discount, discountAmount: 0 } : null
				repriceCart(state, locationId)
				return
			}
			if (action.payload.discount) {
				const discountAmount = discountValueCalculation({
					discountTypeCode: action.payload.discount?.type as discountType,
					discountValue: action.payload.discount?.discount || 0,
					cartTotal: state.cartTotal[locationId],
				});
				state.additionalDiscount[locationId] = { ...action.payload.discount, discountAmount };
				applyCartDiscountToProducts({ state, discountAmount, locationId });
			} else {
				state.additionalDiscount[locationId] = null;
				applyCartDiscountToProducts({ state, discountAmount: 0, locationId });
			}
		},
		addAdditionalCharge: (state, action: PayloadAction<{ charge: initialStateType["additionalCharge"][number], locationId: number }>) => {
			const locationId = action.payload.locationId
			if (usesDiscountEngine(state, locationId)) {
				state.additionalCharge[locationId] = action.payload.charge ? { ...action.payload.charge, amount: 0 } : null
				repriceCart(state, locationId)
				return
			}
			if (action.payload.charge) {
				const chargeAmount = discountValueCalculation({
					discountTypeCode: action.payload.charge?.type as discountType,
					discountValue: action.payload.charge?.charge || 0,
					cartTotal: state.cartTotal[locationId],
				});
				state.additionalCharge[locationId] = { ...action.payload.charge, amount: chargeAmount };
			} else {
				state.additionalCharge[locationId] = null;
			}
			calculateSummary({ state, locationId });
		},
		addTip: (state, action: PayloadAction<{ tip: initialStateType["tip"][number], locationId: number }>) => {
			const locationId = action.payload.locationId
			state.tip[locationId] = action.payload.tip;
			if (usesDiscountEngine(state, locationId)) {
				repriceCart(state, locationId)
				return
			}
			calculateSummary({ state, locationId });
		},
		addCustomerNotes: (state, action: PayloadAction<{ notes: initialStateType["customerNotes"][number], locationId: number }>) => {
			const locationId = action.payload.locationId
			state.customerNotes[locationId] = action.payload.notes;
		},
		addCustomer: (state, action: PayloadAction<{ customerDetails: { details: initialStateType["customerDetails"], locationId: number } }>) => {
			state.customerDetails = action.payload.customerDetails.details;
			// The customer is shared by every location's cart, so every cart is repriced
			Object.keys(state.cartProducts ?? {}).forEach((key) => {
				const locationId = Number(key)
				if (!state.cartProducts[locationId]?.length) return
				if (usesDiscountEngine(state, locationId)) repriceCart(state, locationId)
				else legacyReapply(state, locationId)
			})
		},
		setAutomaticChoice: (state, action: PayloadAction<{ locationId: number, variantId: number, discountId: number | "NONE" }>) => {
			const { locationId, variantId, discountId } = action.payload
			if (!state.automaticChoice[locationId]) state.automaticChoice[locationId] = {}
			state.automaticChoice[locationId][String(variantId)] = discountId
			repriceCart(state, locationId)
		},
		claimOffer: (state, action: PayloadAction<{ locationId: number, discountId: number, getVariantId: number }>) => {
			const { locationId, discountId, getVariantId } = action.payload
			const claims = (state.claimedOffers[locationId] ?? []).filter((claim) => claim.discountId !== discountId)
			state.claimedOffers[locationId] = [...claims, { discountId, getVariantId }]
			repriceCart(state, locationId)
		},
		unclaimOffer: (state, action: PayloadAction<{ locationId: number, discountId: number }>) => {
			const { locationId, discountId } = action.payload
			state.claimedOffers[locationId] = (state.claimedOffers[locationId] ?? []).filter((claim) => claim.discountId !== discountId)
			repriceCart(state, locationId)
		},
		// A discount was created / updated / deleted / toggled: forget cached variants that are not in a cart.
		// Variants in a cart are kept (so the cart keeps its prices) and refetched by the middleware.
		invalidateDiscountCache: (state) => {
			Object.keys(state.discountStore ?? {}).forEach((key) => {
				const locationId = Number(key)
				const cartVariantIds = (state.cartProducts?.[locationId] ?? []).map((item) => item.variantId)
				state.discountStore[locationId] = retainVariants(state.discountStore[locationId], cartVariantIds)
			})
		},
		// Order Settings "Round off" (AsyncStorage) changed or loaded: reprice every sale cart
		setRoundOffSale: (state, action: PayloadAction<boolean>) => {
			if (state.roundOffSale === action.payload) return
			state.roundOffSale = action.payload
			Object.keys(state.cartProducts ?? {}).forEach((key) => {
				const locationId = Number(key)
				if (state.cartProducts[locationId]?.length && usesDiscountEngine(state, locationId)) repriceCart(state, locationId)
			})
		},
		repriceSale: (state, action: PayloadAction<{ locationId: number }>) => {
			const { locationId } = action.payload
			if (usesDiscountEngine(state, locationId)) repriceCart(state, locationId)
		},
		addParkSale: (state, action: PayloadAction<{ name: string, location: { id: number, name: string, timeZone: string }, customerDetails: initialStateType["customerDetails"] }>) => {
			const { additionalCharge, additionalDiscount, automaticDiscount, cartProductIds, cartProducts, customerNotes, manualDiscount, tip, cartQuantity, cartTotal, summary, parkSale, claimedOffers, automaticChoice, pricedCart } = state
			const parkSaleId = parkSale[parkSale?.length - 1]?.id + 1 || 1
			const newParkSale = [
				...parkSale,
				{
					id: parkSaleId,
					date: new Date(),
					details: {
						additionalCharge,
						additionalDiscount,
						automaticDiscount,
						cartProductIds,
						cartProducts,
						customerNotes,
						manualDiscount,
						tip,
						cartTotal,
						cartQuantity,
						summary,
						claimedOffers,
						automaticChoice,
						pricedCart
					},
					location: action.payload.location,
					name: action.payload.name,
					customerDetails: action.payload.customerDetails
				},
			];

			return {
				...initialState,
				parkSale: newParkSale,
				customerDetails: null,
				...remainingState(state)
			};
		},
		clearCart: (state) => {
			const parkSale = [...state.parkSale]
			const customerDetails = state.customerDetails ? { ...state.customerDetails } : null
			return {
				...initialState,
				parkSale,
				customerDetails,
				...remainingState(state)
			}
		},
		// Discounts are refetched and the cart repriced by the middleware after this
		retriveSale: (state, action: PayloadAction<initialStateType["parkSale"][0]>) => {
			return {
				...initialState,
				...action.payload.details,
				customerDetails: action.payload.customerDetails,
				...remainingState(state),
				parkSale: state.parkSale.filter((item) => item.id !== action.payload.id),
				sellScreenType: "SELL"
			} as any;
		},
		resetSale: (state) => {
			const parkSale = [...state.parkSale]
			return {
				...initialState,
				parkSale,
				...remainingState(state)
			}
		},
		setSellScreenType: (state, action: PayloadAction<initialStateType["sellScreenType"]>) => {
			state.sellScreenType = action.payload
		},
		setReturnRequestData: (state, action: PayloadAction<initialStateType["returnRequestData"]>) => {
			state.returnRequestData = action.payload
		},
		setReturnRequestWholeOrderData: (state, action: PayloadAction<ReturnRequestWholeOrder[]>) => {
			state.returnRequestWholeOrderData = action.payload
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(getAllEligibleDiscount.fulfilled, (state, action) => {
				const { cartTotal } = state
				state.getAllEligibleDiscount = action?.payload?.discounts?.overAllDiscounts?.map((item) => {
					let { offerDescription, offerValue } = getOfferValue({ discountValue: item?.discountValue, type: item?.discountType?.discountName, cartTotal: cartTotal[action.meta.arg.payload.locationId], maxAmount: item?.maxAmount ?? undefined, currencySymbol: action?.payload?.currencySymbol })
					return {
						...item,
						offerValue,
						offerDescription
					}
				})
			})
			.addCase(getAllEligibleDiscount?.rejected, () => {
				ZenToast({ type: "error", message: "Failed to get a eligible discounts" })
			})
			// Product Details
			.addCase(getProductDetailsById?.pending, (state) => {
				state.productDetailsLoader = true
			})
			.addCase(getProductDetailsById.fulfilled, (state, action) => {
				state.productsDetailsById[action.payload.productData.id] = {
					...action.payload.productData,
					lastFetchedAt: Date.now(),
				};
				state.productDetailsLoader = false
			})
			.addCase(getProductDetailsById?.rejected, (state, action) => {
				const errorMessage = action?.payload?.error?.details || action?.payload?.error?.message || action?.error?.message || "Failed to get a product details";
				ZenToast({ type: "error", message: errorMessage });
				state.productDetailsLoader = false;
			})


			// Variant discounts: normalised once, deduplicated by discount id, cached per location
			.addCase(getOneVariantDiscount.fulfilled, (state, action) => {
				const { variantId, locationId } = action?.meta?.arg
				if (!state.discountStore) state.discountStore = {}
				state.discountStore[locationId] = mergeVariantDiscounts(
					state.discountStore[locationId] ?? emptyDiscountStore(),
					variantId,
					normaliseProductDiscount(action.payload?.productDiscount)
				)
				if (state.cartProducts?.[locationId]?.length && usesDiscountEngine(state, locationId)) {
					repriceCart(state, locationId)
				}
			})
			.addCase(getOneVariantDiscount?.rejected, () => {
				ZenToast({ type: "error", message: "Failed to get a variant discount" })
			})
			.addCase(claimOfferForCart.rejected, (_state, action) => {
				if (action.payload) ZenToast({ type: "warning", message: action.payload })
			})
	}
});

export const {
	addCartProduct,
	deleteCartProduct,
	updateCartProductQuantity,
	addManualDiscount,
	addAdditionalDiscount,
	addAdditionalCharge,
	addTip,
	addCustomerNotes,
	addParkSale,
	retriveSale,
	clearCart,
	addCustomer,
	resetSale,
	setSellScreenType,
	setReturnRequestData,
	setReturnRequestWholeOrderData,
	setAutomaticChoice,
	claimOffer,
	unclaimOffer,
	repriceSale,
	invalidateDiscountCache,
	setRoundOffSale
} = newSellscreenSlice.actions;

export default newSellscreenSlice.reducer;
